import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  makeStyles,
  Menu,
  Button,
  Dropzone,
  IconButton,
  MenuItem,
  Spinner,
  Typography,
  useClientOptions,
  useDialogs
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { QuoteFile } from 'paintscout'
import { PERMITTED_FILE_TYPES } from '@paintscout/util/s3'
import { hasIntegrationInterface } from '@paintscout/util/builder'
import { EditIntegrationItemFilesDialog, EditItemFilesDialog } from '@paintscout/react-quote'
import type { DropzoneRef } from 'react-dropzone'
import AddIcon from '@material-ui/icons/Add'
import MediaIcon from '@material-ui/icons/PermMedia'

const useStyles = makeStyles<Theme, FilesDocListProps>(
  (theme) => ({
    root: {
      border: 'none'
    },
    dropBoxContainer: {
      padding: `${theme.spacing(1)}px ${theme.spacing(2.5)}px`
    },
    fileListContainer: {
      display: 'flex',
      flexDirection: 'column',
      transition: 'max-height 0.3s ease',
      overflow: 'hidden',
      // last item does not have border bottom
      '& > div:last-child': {
        borderBottom: 'none'
      }
    },
    fileItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      width: '100%'
    },
    fileItemTitleDiv: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(6)
    },
    fileItemTitle: { textTransform: 'none', fontWeight: 'bold' },
    fileItemTitleImg: { width: 36, height: 43, objectFit: 'fill' },
    spinnerDiv: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '160px', border: 'none' },
    buttonDiv: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '58px'
    },
    dateCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    downloadIcon: {
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          color: theme.paintscout.palette.primary
        }
      }
    }
  }),
  { name: 'FilesDocList' }
)

export interface FilesDocListProps {
  classes?: StyleClasses<typeof useStyles>
  files: QuoteFile[]
  docId: string
  onUpload: (files: QuoteFile[]) => Promise<void>
  disabled?: boolean
}

function FilesDocList(props: FilesDocListProps) {
  const classes = useStyles(props)
  const { docId, files, onUpload, disabled = false } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { options } = useClientOptions()
  const hasCompanyCam = hasIntegrationInterface({ options, integration: 'companycam' })
  const [loading, setLoading] = useState(false)
  const [userExpanded, setUserExpanded] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const showAllFiles = userExpanded || files.length <= 3
  const fileListRef = useRef<HTMLDivElement>(null)
  const dropzoneRef = useRef<DropzoneRef>(null)

  useEffect(() => {
    if (fileListRef.current && !showAllFiles) {
      fileListRef.current.style.maxHeight = '230px'
    }
  }, [])

  useEffect(() => {
    if (fileListRef.current) {
      fileListRef.current.style.maxHeight = showAllFiles ? `${fileListRef.current.scrollHeight}px` : '230px'
    }
  }, [showAllFiles, files])

  async function confirmUpload(finalFiles: QuoteFile[]) {
    setLoading(true)
    try {
      await onUpload(finalFiles)
      dismissDialog()
    } catch (err) {
      console.error('File upload failed', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleDrop(uploadedFiles: File[]) {
    setLoading(true)
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      titleOnly: true,
      uploadFiles: uploadedFiles,
      files,
      index: files.length,
      onConfirm: confirmUpload,
      onCancel: () => {
        dismissDialog()
        setLoading(false)
      },
      onDelete: (_key, _files) => {}
    })
  }

  function handleUploadButtonClick(e: React.MouseEvent<HTMLElement>) {
    setMenuAnchorEl(e.currentTarget)
  }

  function handleMenuClose() {
    setMenuAnchorEl(null)
  }

  function handleDeviceClick() {
    handleMenuClose()
    dropzoneRef.current?.open()
  }

  function handleCompanyCamClick() {
    handleMenuClose()
    openDialog(EditIntegrationItemFilesDialog, {
      provider: 'companycam',
      maxWidth: 'lg',
      fullWidth: true,
      files,
      onConfirm: (newFiles: QuoteFile[]) => {
        const now = Date.now()
        const existingKeys = new Set(files.map((f) => f.key))
        const timestampedNewFiles = newFiles
          .filter((f) => !existingKeys.has(f.key))
          .map((f) => ({ ...f, created: f.created ?? now }))
        const mergedFiles = [...files, ...timestampedNewFiles]
        openDialog(
          EditItemFilesDialog,
          {
            fileDocId: docId,
            files: mergedFiles,
            index: files.length,
            titleOnly: true,
            onConfirm: confirmUpload,
            onCancel: dismissDialog,
            onDelete: (_key, _files) => {}
          },
          { replace: true }
        )
      },
      onCancel: dismissDialog
    })
  }

  function handleFileClick(index: number) {
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      files,
      index,
      viewOnly: disabled,
      titleOnly: true,
      onDelete: (key) => {
        console.log('key of deletedfile', { key })
      },
      onConfirm: (newFiles, _deletedFiles) => {
        console.log('new and delted filesv', { newFiles })
        dismissDialog()
        onUpload(newFiles)
      },
      onCancel: dismissDialog
    })
  }

  function handleDownload(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, file: QuoteFile) {
    e.stopPropagation()
    if (!file?.src) {
      return
    }

    fetch(file.src, { mode: 'cors' })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = file.title || file.src.split('/').pop() || 'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      })
      .catch(() => {
        // fallback: open in new tab if download fails
        window.open(file.src, '_blank')
      })
  }

  return (
    <Card className={classes.root}>
      <div className={classes.dropBoxContainer}>
        {loading ? (
          <div className={classes.spinnerDiv}>
            <Spinner size={30} />
          </div>
        ) : hasCompanyCam && !disabled ? (
          <>
            <Dropzone
              noClick={true}
              dropzoneRef={dropzoneRef}
              onDrop={handleDrop}
              accept={PERMITTED_FILE_TYPES.join(',')}
              style={{ height: '58px', marginTop: 20, marginBottom: 5 }}
              shouldRenderPreview={true}
              renderPreview={() => (
                <Button
                  variant="outlined"
                  fullWidth={true}
                  icon={AddIcon}
                  onClick={handleUploadButtonClick}
                  className={undefined}
                  style={{ height: '100%' }}
                >
                  Upload
                </Button>
              )}
            />
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
              <MenuItem icon={MediaIcon} onClick={handleDeviceClick}>
                My Device
              </MenuItem>
              <MenuItem
                image="https://res.cloudinary.com/taptapquote/image/upload/v1641926710/paintscout/providers/companycam.png"
                onClick={handleCompanyCamClick}
              >
                CompanyCam
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Dropzone
            text="Upload"
            disabled={disabled}
            onDrop={handleDrop}
            accept={PERMITTED_FILE_TYPES.join(',')}
            style={{ height: '58px', marginTop: 20, marginBottom: 5 }}
            buttonFullHeight={true}
            {...props}
          />
        )}
        <Typography variant="subtitle1" align="left">
          Max file size 100MB
        </Typography>
      </div>
      <div ref={fileListRef} className={classes.fileListContainer}>
        {files.map((file, index) => (
          <div key={index} className={classes.fileItem} onClick={() => handleFileClick(index)}>
            <div className={classes.fileItemTitleDiv}>
              <img src={file.src} alt={file.title} className={classes.fileItemTitleImg} />
              <Typography variant="h4" className={classes.fileItemTitle} color="textPrimary">
                {file.title}
              </Typography>
            </div>
            <div>
              <Typography variant="body1" color="textSecondary" className={classes.dateCell}>
                {getFormattedDate(file.created)}
                <IconButton className={classes.downloadIcon} size="small" onClick={(e) => handleDownload(e, file)}>
                  <i className="fa-solid fa-arrow-down-to-line" />
                </IconButton>
              </Typography>
            </div>
          </div>
        ))}
        {files.length === 0 && (
          <Card className={classes.spinnerDiv}>
            <Typography variant="subtitle1">No files found.</Typography>
          </Card>
        )}
      </div>
      {files.length > 3 && (
        <div className={classes.buttonDiv}>
          <Button
            variant="text"
            color="primary"
            onClick={() => setUserExpanded(!userExpanded)}
            style={{ margin: '10px 0' }}
          >
            {showAllFiles ? 'Show Less' : 'Show More'}
          </Button>
        </div>
      )}
    </Card>
  )
}

export default FilesDocList

const getFormattedDate = (dateString: number): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}
